import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector, useDispatch, batch } from 'react-redux'
import {
  Card,
  Input,
  Select,
  Typography,
  useInput,
  useSelect,
  Spinner,
  useViewport
} from '@chilecompra/react-kit/components'
import { checkInputErrorRequired, formatDNI } from '@chilecompra/react-kit/utils'
import { IDENTIFICATION_TYPE } from '../../../config/settings/constants'
import { checkErrorDniInRepresentatives } from '../../../modules/utils/checkers'
import { isNaturalPerson } from '../../../modules/utils/helpers'
import {
  onGetSignerInfoThunk,
  onGetRepresentativesThunk,
  onSetSignerSuccess,
  onSetHasError
} from '../RegisterWizard.actions'
import {
  Form,
  Grid,
  Search,
  SearchIcon,
  SearchLoadingIcon,
  GridUTP,
  SpinnerRoot,
  ToastFontContainer,
  Divider,
  Strong
} from './SignerIdentification.styles'
import { Toast } from '@chilecompra/react-kit'

/**
 * The Signer Identification section container.
 */
const SignerIdentification = () => {
  const dispatch = useDispatch()
  const {
    signer,
    disabled,
    error,
    hasError,
    errorMainService,
    identifierType,
    loading,
    representative: representativeList,
    searching,
    isCivilRegistry
  } = useSelector(state => state.registerWizard.signerIdentification)
  const [searchDni, setSearchDni] = useState(false)
  const representativeAux = useMemo(() => [{ name: 'Otro RUT', value: IDENTIFICATION_TYPE.DNI }], [])
  const { size } = useViewport()

  // Create options for the drop-down list in this format: 'RUT/ID First name Last name'.
  // Also add default options other
  const representativeOptions = useCallback(() => {
    const options = representativeList.map(representantive => {
      return {
        name: `${representantive.dni} ${[
          [representantive.name, representantive.lastNamePaternal].join(' ').trim(),
          representantive.lastNameMaternal
        ]
          .join(' ')
          .trim()}`,
        value: representantive.dni
      }
    })
    return options.concat(representativeAux)
  }, [representativeList, representativeAux])

  // To load the representative list from SII... just load when is not signed
  // when is signed, the disabled value is true
  useEffect(() => {
    if (disabled) {
      return
    }
    dispatch(onGetRepresentativesThunk())
  }, [disabled, dispatch])

  // Update the signer selected value in the store when the selector changes
  const changeCallbackSelect = updatedValue => {
    if (
      !disabled &&
      !loading &&
      (updatedValue === IDENTIFICATION_TYPE.DNI || updatedValue === IDENTIFICATION_TYPE.OTHER)
    ) {
      // To reset the signer value in the store and the selected value (identifierType)
      dispatch(
        onSetSignerSuccess({
          signer: {
            dni: '',
            fullName: '',
            name: '',
            lastNamePaternal: '',
            lastNameMaternal: '',
            gender: ''
          },
          identifierType: updatedValue,
          isCivilRegistry: updatedValue === IDENTIFICATION_TYPE.DNI
        })
      )
      setSearchDni(false)
    }
    // When select one of the representatives
    if (
      !disabled &&
      !loading &&
      updatedValue !== IDENTIFICATION_TYPE.DNI &&
      updatedValue !== IDENTIFICATION_TYPE.OTHER
    ) {
      const dni = representativeList.find(item => item.dni === updatedValue)?.dni
      const fullName = representativeList.find(item => item.dni === updatedValue)?.fullName
      dispatch(
        onSetSignerSuccess({
          signer: {
            dni,
            fullName
          },
          identifierType: dni,
          isCivilRegistry: true
        })
      )
      setSearchDni(false)
    }
  }

  // All inputs initialization
  const {
    error: signerSelectError,
    value: signerSelectValue,
    onChange: handleSignerSelectChange
  } = useSelect({
    initialValue: identifierType,
    changeCallback: changeCallbackSelect,
    overwriteCallback: () => identifierType
  })
  const {
    error: dniError,
    value: dniValue,
    onChange: handleDniChange
  } = useInput({
    initialValue: signer?.dni,
    errorCallbacks: [
      checkInputErrorRequired('Campo obligatorio'),
      identifierType === IDENTIFICATION_TYPE.DNI &&
        checkErrorDniInRepresentatives(representativeList, 'RUT ya existe en lista de representantes')
    ].filter(Boolean),
    formatCallbacks: [formatDNI],
    overwriteCallback: () => signer?.dni
  })
  // Update the DNI in the store
  useEffect(() => {
    if (signerSelectValue === IDENTIFICATION_TYPE.DNI && dniValue !== signer?.dni) {
      dispatch(
        onSetSignerSuccess({
          signer: {
            dni: dniValue,
            fullName: signer?.fullName,
            name: signer?.name,
            lastNamePaternal: signer?.lastNamePaternal,
            lastNameMaternal: signer?.lastNameMaternal,
            gender: signer?.gender
          },
          identifierType: IDENTIFICATION_TYPE.DNI,
          isCivilRegistry: !hasError
        })
      )
    }
  }, [dniValue, dispatch, signer, hasError, signerSelectValue])

  // when a registered user is selected
  const handleRegisterDNI = useCallback(() => {
    const dniFormat = formatDNI(identifierType, 2)
    batch(() => {
      dispatch(onGetSignerInfoThunk({ dni: dniFormat }))
      dispatch(onSetHasError({ hasError: false }))
    })
  }, [identifierType, dispatch])

  useEffect(() => {
    if (identifierType !== 2 && identifierType !== 3 && !disabled) {
      handleRegisterDNI()
    }
  }, [identifierType, disabled, handleRegisterDNI])

  // When DNI changes and it is valid, then the endpoint is called to get the info from the civil registry
  const handleSearchDNI = event => {
    event.preventDefault()
    const dniFormat = formatDNI(dniValue, 2)
    if (signerSelectValue === IDENTIFICATION_TYPE.DNI) {
      setSearchDni(true)
      if (!dniError && isNaturalPerson(dniValue)) {
        batch(() => {
          dispatch(onGetSignerInfoThunk({ dni: dniFormat }))
          dispatch(onSetHasError({ hasError: false }))
        })
      }
    }
  }

  // When some input has an error
  useEffect(() => {
    if (
      signerSelectValue === IDENTIFICATION_TYPE.DNI &&
      (dniError || (error !== undefined && error.reason !== 'SOMETHING_WENT_WRONG_ERROR'))
    ) {
      batch(() => {
        dispatch(
          onSetSignerSuccess({
            signer: {
              dni: dniValue,
              fullName: '',
              name: '',
              lastNamePaternal: '',
              lastNameMaternal: '',
              gender: ''
            },
            identifierType: IDENTIFICATION_TYPE.DNI,
            isCivilRegistry: true
          })
        )
        dispatch(onSetHasError({ hasError: true }))
      })
    }
  }, [dniError, dispatch, dniValue, error, signerSelectValue])

  const dniErrorText = useCallback(() => {
    if (dniError !== '') return dniError

    if (error !== undefined) {
      switch (error.reason) {
        case 'SOMETHING_WENT_WRONG_ERROR':
          return 'Ocurrió un error con el registro civil.'
        case 'DNI_DOES_NOT_MATCH_ERROR':
          return 'El RUT ingresado no se encuentra.'
        case 'DECEASED_PERSON_ERROR':
          return 'El RUT ingresado pertenece a una persona fallecida.'
      }
    }

    return ''
  }, [dniError, error])

  const signerInformation = (
    <>
      Ingresa el RUT de la persona que firmará el convenio de colaboración con la Dirección de Contratación y Compras
      Públicas. Debe ser un Representante Legal de tu institución. Podrás seleccionar alguno de los Representantes
      Legales obtenidos del Servicio de Impuestos Internos o agregar uno nuevo.
      <p />
      Si la información no se encuentra actualizada, debes realizar su modificación directamente en el Servicio de
      Impuestos Internos.
    </>
  )

  return errorMainService ? null : (
    <>
      {loading && (
        <>
          {loading && (
            <Card enableBorder color="blank" margin="0 0 10px" padding="20px 20px 10px">
              <SpinnerRoot role="status" aria-label="Cargando...">
                <Spinner />
              </SpinnerRoot>
            </Card>
          )}
        </>
      )}
      {!loading && (
        <Card enableBorder color="blank" margin="20px 0 10px 0" padding="20px 20px 10px">
          <Typography tabIndex="0" fontWeight="bold" margin="0 0 20px 0" variant="h4">
            Identificación del firmante
          </Typography>
          <Typography tabIndex="0" margin="0 0 10px 0" variant="body2">
            {signerInformation}
          </Typography>
          <Grid
            container
            spacing={1}
            display={size.isTiny ? 'flex' : ''}
            flexDirection={size.isTiny ? 'column' : 'row'}
            margin="10px 0"
          >
            <GridUTP tabIndex="0" item padding="2px 4px 4px 0" width="160px">
              <Typography fontWeight="bold" variant="body2" width="150px">
                RUT del firmante
              </Typography>
              <Typography fontSize="12px" variant="subtitle">
                Fuente: Servicio de Impuestos Internos
              </Typography>
            </GridUTP>
            <Grid margin={size.isTiny ? '10px 0' : ''} width={size.isTiny || size.isDownToLarge ? '100%' : '317px'}>
              <Select
                disabled={disabled}
                error={
                  identifierType !== IDENTIFICATION_TYPE.DNI && identifierType !== IDENTIFICATION_TYPE.OTHER
                    ? dniErrorText() || signerSelectError
                    : null
                }
                info="En caso de firmar con Clave Única, el RUT deberá coincidir."
                label="Representante Legal"
                aria-label="Representante Legal"
                loading={
                  identifierType !== IDENTIFICATION_TYPE.DNI &&
                  identifierType !== IDENTIFICATION_TYPE.OTHER &&
                  searching
                }
                options={representativeOptions()}
                value={signerSelectValue}
                onChange={handleSignerSelectChange}
              />
            </Grid>
            {signerSelectValue && signerSelectValue === IDENTIFICATION_TYPE.DNI && (
              <Grid
                width={size.isTiny || size.isDownToLarge ? '100%' : '260px'}
                margin={size.isTiny || size.isDownToLarge ? '' : '0 0 0 10px'}
              >
                <Form onSubmit={handleSearchDNI}>
                  <Input
                    disabled={disabled || searching}
                    error={dniErrorText()}
                    label="RUT"
                    aria-label="RUT"
                    maxLength={15}
                    suffix={
                      <Search
                        tabIndex="0"
                        aria-label="Buscar Rut"
                        disabled={dniValue === '' || searching || disabled}
                        type="submit"
                      >
                        {!searching && <SearchIcon />}
                        {searching && <SearchLoadingIcon />}
                      </Search>
                    }
                    value={dniValue}
                    width="100%"
                    onChange={handleDniChange}
                  />
                </Form>
              </Grid>
            )}
          </Grid>
        </Card>
      )}
      {signerSelectValue && signerSelectValue === IDENTIFICATION_TYPE.DNI && searchDni && !isCivilRegistry && (
        <>
          <ToastFontContainer>
            <Toast severity="warning">
              <Strong>Completa la información en el Convenio de Adhesión</Strong>
              <Divider />
              Debido a que no hemos podido obtener la información del Registro Civil, es necesario que completes en el
              documento el nombre y apellido de la persona que firma.
            </Toast>
          </ToastFontContainer>

          <ToastFontContainer>
            <Toast severity="info">
              <Strong>Algunos datos no se han podido cargar</Strong>
              <Divider />
              Por favor diríjase a la sección correspondiente que dice: <Strong>
                "La personería de don/doña..."
              </Strong>{' '}
              y complete los espacios en blanco manualmente con la información requerida.
            </Toast>
          </ToastFontContainer>
        </>
      )}
    </>
  )
}

export default SignerIdentification
